import { useState } from 'react';
import { Modal } from '@safetyhub/ui';
import {
  SiteAssignment,
  OrganizationMember,
  AssignmentType,
  getUserIdentifier,
} from '../lib/organizations';
import { RoleBadge } from './RoleBadge';
import { memberDisplayName } from '../lib/members';
import { formatDateToLocal } from '@safetyhub/shared';
import {
  canRenderAssignmentTypeChoice,
  resolvePrimaryDesignation,
  toDesignationAssignment,
  type PrimaryDesignationAssignment,
} from '../lib/sitePrimaryDesignation';

/**
 * 담당자 상세 모달 — 데스크톱·모바일이 **공유**한다(`SiteDetail/index.tsx`가 한 번 렌더하고
 * 두 레이아웃은 `setSelectedAssignment`만 한다). 그래서 판정을 여기 한 자리에 두면 양쪽이 함께 고쳐진다.
 *
 * 담당자 유형(정/부) 변경의 게이트는 `lib/sitePrimaryDesignation.ts`가 소유한다 — 결정 기록
 * `docs/decisions/2026-09-26-primary-assignee-designation-wall.md`, 서버 정본은
 * `apps/server/src/sites/sites.service.ts` `updateAssignment`.
 */

interface AssigneeDetailModalProps {
  assignment: SiteAssignment;
  member: OrganizationMember;
  /**
   * 담당자 **유형 변경**(`site.assign`) — 서버 `updateAssignment`의 권한 키. 이 값만으로는 부족하고
   * 상황별 벽(`resolvePrimaryDesignation`)과 AND다.
   *
   * ⚠️ `canUnassign`과 **다른 축**이다. 서버가 `assignMember`·`unassignMember`는 `isOwnerMember`,
   * `updateAssignment`는 `site.assign`으로 갈라 두었는데 화면이 오래도록 하나로 합쳐 읽었다.
   */
  canChangeAssignmentType: boolean;
  /**
   * 할당 **해제**(서버 `unassignMember`가 `isOwnerMember`) — 소유권 성질.
   *
   * ⚠️ 이 버튼은 **대상이 조직 소유자면 감춘다**(`canUnassign && !targetIsOrgOwner`). 담당자 **유형
   * 변경**은 2026-09-26부터 그러지 **않는다** — 두 조건이 갈리는 것이 의도다. 「일관성」을 이유로
   * 합치지 말 것: 「소유자의 정/부를 바꿀 수 있나」와 「소유자를 현장에서 아예 떼어낼 수 있나」는
   * 다른 질문이고, 서버 게이트도 각각 `site.assign`과 `isOwnerMember`로 다르다.
   * 근거: `lib/sitePrimaryDesignation.ts`의 `PrimaryDesignationDenialReason` 주석.
   */
  canUnassign: boolean;
  /** 보는 사람의 CompanyMember.id — 벽의 「본인」 갈래. 모르면 `undefined`(그 갈래가 성립하지 않는다). */
  viewerMemberId: string | undefined;
  /** 소유권 성질(`usePermissions().isOwner`) — 벽의 소유자 갈래. */
  isOwner: boolean;
  /** `site.manage` — 공석 충원에서 **자기 승격**일 때만 쓴다. */
  canManageSite: boolean;
  /** 그 현장의 배정 전량 — 현 정 담당자를 여기서 찾는다(공석인가 / 그 사람이 나인가). */
  assignments: PrimaryDesignationAssignment[];
  onClose: () => void;
  onUpdateType: (assignmentType: AssignmentType) => Promise<void>;
  onUnassign: () => void;
}

const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
  PRIMARY: '정 담당자',
  SECONDARY: '부 담당자',
};

export function AssigneeDetailModal({
  assignment,
  member,
  canChangeAssignmentType,
  canUnassign,
  viewerMemberId,
  isOwner,
  canManageSite,
  assignments,
  onClose,
  onUpdateType,
  onUnassign,
}: AssigneeDetailModalProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const currentType = assignment.assignmentType || 'SECONDARY';
  const targetIsOrgOwner = member.isOwner === true;

  // 대상이 조직 소유자인지는 **여기서 보지 않는다**(2026-09-26 결정 — 해제 버튼에만 남는 축).
  const showTypeChoice = canRenderAssignmentTypeChoice({ canAssign: canChangeAssignmentType });

  /** 버튼마다 따로 판정한다 — 「무엇이 왜 막혔나」가 값어치다(막힌 사실만으론 다음 행동을 모른다). */
  const verdictFor = (type: AssignmentType) =>
    resolvePrimaryDesignation({
      nextType: type,
      target: toDesignationAssignment(assignment),
      assignments,
      viewerMemberId,
      isOwner,
      canAssign: canChangeAssignmentType,
      canManageSite,
    });

  const typeChoices = (['PRIMARY', 'SECONDARY'] as AssignmentType[]).map((type) => ({
    type,
    verdict: verdictFor(type),
  }));
  /** 막힌 버튼의 사유 — 둘 다 막히는 조합은 없지만(하나는 늘 현재 값) 열거해 첫 사유를 낸다. */
  const blockedReason = typeChoices.find((c) => !c.verdict.allowed)?.verdict;

  const handleTypeChange = async (newType: AssignmentType) => {
    if (newType === currentType || isUpdating) return;
    // 서버가 정본이지만(`updateAssignment`) 어포던스와 실제 호출이 갈리면 사용자가 「눌렸는데 실패」를
    // 본다 — 화면 판정이 막은 것은 호출도 하지 않는다.
    if (!verdictFor(newType).allowed) return;

    setIsUpdating(true);
    try {
      await onUpdateType(newType);
    } finally {
      setIsUpdating(false);
    }
  };

  // 부모(`AssigneeDetailModalWrapper`)가 열 때만 마운트하므로 open은 늘 true다. 헤더(제목·✕)·
  // 오버레이·Esc·포커스는 base가 소유한다 — 종전 raw 오버레이엔 Esc·포커스 트랩이 없었다.
  return (
    <Modal
      open
      onClose={onClose}
      title="담당자 정보"
      size="md"
      footer={
        <>
          {/* 해제는 서버가 소유자 전용(`unassignMember` isOwnerMember) — 유형 변경 축과 갈라 읽는다. */}
          {canUnassign && !targetIsOrgOwner && (
            <button
              onClick={onUnassign}
              className="mr-auto text-sm text-red-600 hover:text-red-800"
            >
              할당 해제
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-ink-700 bg-white border border-line rounded-card-sm hover:bg-surface-hover"
          >
            닫기
          </button>
        </>
      }
    >
          <div className="space-y-4">
            {/* 프로필 */}
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-full bg-brand-100 flex items-center justify-center">
                <span className="text-brand-600 text-lg font-medium">
                  {memberDisplayName(member, 'U').charAt(0)}
                </span>
              </div>
              <div className="ml-4">
                <div className="flex items-center gap-2">
                  <p className="text-lg font-medium text-ink-900">{memberDisplayName(member)}</p>
                  <RoleBadge member={member} />
                </div>
                <p className="text-sm text-ink-500">
                  {getUserIdentifier(member.user?.auths ?? [])}
                </p>
              </div>
            </div>

            {/* 상세 정보 */}
            <div className="bg-surface-soft rounded-card p-4 space-y-3">
              {member.user?.phone && (
                <div className="flex justify-between">
                  <span className="text-sm text-ink-500">전화번호</span>
                  <span className="text-sm text-ink-900">{member.user?.phone}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-ink-500">할당일</span>
                <span className="text-sm text-ink-900">
                  {formatDateToLocal(new Date(assignment.createdAt))}
                </span>
              </div>
            </div>

            {/* 담당자 타입 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                담당자 유형
              </label>
              {showTypeChoice ? (
                <div className="flex gap-2">
                  {typeChoices.map(({ type, verdict }) => (
                    <button
                      key={type}
                      onClick={() => handleTypeChange(type)}
                      disabled={isUpdating || !verdict.allowed}
                      title={verdict.allowed ? undefined : verdict.message}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-card-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        currentType === type
                          ? 'bg-brand-600 text-white border-brand-600'
                          : 'bg-white text-ink-700 border-line hover:bg-surface-hover'
                      }`}
                    >
                      {isUpdating && currentType !== type ? '변경 중...' : ASSIGNMENT_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
              ) : (
                // 이 자리에 오는 이유는 **권한 키 부재** 하나다. 옛 「(Owner는 변경 불가)」 꼬리표는
                // 2026-09-26에 걷었다 — 대상이 조직 소유자여도 이제 유형은 바꿀 수 있다.
                <div className="px-4 py-2 bg-surface-hover rounded-card-sm text-sm text-ink-700">
                  {ASSIGNMENT_TYPE_LABELS[currentType]}
                </div>
              )}
              {blockedReason && !blockedReason.allowed && (
                // 사유를 본문에 낸다 — `title`만 두면 모바일(터치)에서 영영 안 보인다.
                <p className="mt-2 text-xs text-amber-700" role="note">
                  {blockedReason.message}
                </p>
              )}
              <p className="mt-2 text-xs text-ink-500">
                정 담당자는 현장 정보를 수정할 수 있고, 새로 만드는 보고서의 결재란 기본 점유자가 됩니다.
              </p>
            </div>

            {/* (옛 「점검 관리 권한 (현장별)」 3상태 오버라이드는 P7-a에서 서버 컬럼과 함께 제거 —
                점검 권한은 역할 키 inspection.create·inspection.manage + 현장 배정으로 판정한다) */}
          </div>
    </Modal>
  );
}
